import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  plantillaPorDefecto,
  type ClausulaContrato,
  type PlantillaContrato,
} from '@/lib/contratos/plantillasPorDefecto';

export type TipoContrato = 'seguridad' | 'pci';

export interface PlantillaContratoDB {
  id: string;
  empresa_id: string;
  tipo: TipoContrato;
  nombre: string;
  objeto: string;
  clausulas: ClausulaContrato[];
  texto_legal: string;
  activa: boolean;
  created_at: string;
  updated_at: string;
}

export function usePlantillasContratos() {
  const { perfil } = useAuth();
  const { toast } = useToast();
  const [plantillas, setPlantillas] = useState<PlantillaContratoDB[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!perfil?.empresa_id) return;
    setLoading(true);
    const { data, error } = await (supabase as any)
      .from('contrato_plantillas')
      .select('*')
      .eq('empresa_id', perfil.empresa_id)
      .order('tipo');
    if (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } else {
      setPlantillas((data ?? []) as PlantillaContratoDB[]);
    }
    setLoading(false);
  }, [perfil?.empresa_id, toast]);

  useEffect(() => { load(); }, [load]);

  /** Devuelve la plantilla activa del tipo o el default si no existe. */
  const getPlantilla = useCallback(
    (tipo: TipoContrato): PlantillaContrato => {
      const p = plantillas.find(x => x.tipo === tipo && x.activa);
      if (p) {
        return {
          tipo: p.tipo,
          nombre: p.nombre,
          objeto: p.objeto,
          clausulas: p.clausulas,
          texto_legal: p.texto_legal,
        };
      }
      return plantillaPorDefecto(tipo);
    },
    [plantillas],
  );

  const upsertPlantilla = async (tipo: TipoContrato, patch: Partial<PlantillaContrato>) => {
    if (!perfil?.empresa_id) throw new Error('Sin empresa');
    const existente = plantillas.find(p => p.tipo === tipo);
    const base = existente
      ? { nombre: existente.nombre, objeto: existente.objeto, clausulas: existente.clausulas, texto_legal: existente.texto_legal }
      : plantillaPorDefecto(tipo);
    const payload = {
      empresa_id: perfil.empresa_id,
      tipo,
      nombre: patch.nombre ?? base.nombre,
      objeto: patch.objeto ?? base.objeto,
      clausulas: (patch.clausulas ?? base.clausulas) as any,
      texto_legal: patch.texto_legal ?? base.texto_legal,
      activa: true,
    };
    if (existente) {
      const { error } = await (supabase as any)
        .from('contrato_plantillas')
        .update(payload)
        .eq('id', existente.id);
      if (error) throw error;
    } else {
      const { error } = await (supabase as any).from('contrato_plantillas').insert(payload);
      if (error) throw error;
    }
    await load();
  };

  const restaurarPorDefecto = async (tipo: TipoContrato) => {
    const def = plantillaPorDefecto(tipo);
    await upsertPlantilla(tipo, def);
  };

  return { plantillas, loading, getPlantilla, upsertPlantilla, restaurarPorDefecto, reload: load };
}
